import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listNotifications, markRead, unreadCount } from '../api/notifications';
import { getTokenStore } from '../api/token-store';
import { getSocket } from '../socket/socket';
import { WS_EVENTS } from '../socket/chat-events';

export const UNREAD_COUNT_KEY = ['notifications', 'unread-count'] as const;
export const NOTIFICATIONS_KEY = ['notifications', 'list'] as const;

export function useGlobalSocket() {
  const qc = useQueryClient();

  useEffect(() => {
    const token = getTokenStore().getAccessToken();
    if (!token) return;
    const socket = getSocket();
    if (!socket.connected) socket.connect();

    function onNotificationCreated(payload: any) {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['admin', 'tx'] });
      qc.invalidateQueries({ queryKey: ['transaction'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['scope-form'] });

      const txId = (payload?.data?.transactionId || '')?.toLowerCase();
      const currentPath = typeof window !== 'undefined' ? window.location.pathname.toLowerCase() : '';
      if (txId && currentPath.includes(txId)) {
        return;
      }
      qc.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
      qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    }

    function onTxCreated() {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['admin', 'tx'] });
      qc.invalidateQueries({ queryKey: ['transaction'] });
    }

    function onTxUpdated() {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['admin', 'tx'] });
      qc.invalidateQueries({ queryKey: ['transaction'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['scope-form'] });
    }

    function onNotificationRead() {
      qc.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
      qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    }

    socket.on('notification:created', onNotificationCreated);
    socket.on('notification:read', onNotificationRead);
    socket.on(WS_EVENTS.TX_CREATED, onTxCreated);
    socket.on(WS_EVENTS.TX_UPDATED, onTxUpdated);

    return () => {
      socket.off('notification:created', onNotificationCreated);
      socket.off('notification:read', onNotificationRead);
      socket.off(WS_EVENTS.TX_CREATED, onTxCreated);
      socket.off(WS_EVENTS.TX_UPDATED, onTxUpdated);
    };
  }, [qc]);
}

export function useUnreadCount() {
  useGlobalSocket();

  return useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: unreadCount,
    enabled: !!getTokenStore().getAccessToken(),
    staleTime: 20_000,
    refetchInterval: 60_000,
  });
}

export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => listNotifications({ limit: 30 }),
    enabled: enabled && !!getTokenStore().getAccessToken(),
    staleTime: 15_000,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
      qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}
